import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  patrocinadoresLocales,
  tiposPatrocinadorLocal,
  displaysPatrocinadorLocal,
  metricasGlobalesPatrocinadoresLocales,
  reglasSistemaPatrocinadoresLocales,
  getPatrocinadoresActivos,
  getTopPatrocinadoresPorInversion,
  getTopPatrocinadoresPorRendimiento,
  formatearDinero,
  formatearNumero,
  type TipoPatrocinadorLocal,
  type PatrocinadorLocal,
} from '@/mocks/patrocinadoresLocales';

const mg = metricasGlobalesPatrocinadoresLocales;
const activos = getPatrocinadoresActivos();
const topInversion = getTopPatrocinadoresPorInversion(8);
const topRendimiento = getTopPatrocinadoresPorRendimiento(6);
const ciudades = [...new Set(patrocinadoresLocales.map(p => p.ciudad))].sort();

type TabId = 'patrocinadores' | 'metricas' | 'reglas';

function TipoBadge({ tipo }: { tipo: TipoPatrocinadorLocal }) {
  const cfg = tiposPatrocinadorLocal.find(t => t.id === tipo);
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${cfg.bg} ${cfg.color}`}>
      <i className={`${cfg.icono} text-[9px]`}></i>
      {cfg.label}
    </span>
  );
}

function DisplayBadge({ display }: { display: string }) {
  const cfg = displaysPatrocinadorLocal.find(d => d.id === display);
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium ${cfg.bg} ${cfg.color}`}>
      <i className={`${cfg.icono} text-[7px]`}></i>
      {cfg.label}
    </span>
  );
}

function MetricaMiniBar({ label, value, max, color, icono }: { label: string; value: number; max: number; color: string; icono: string }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div className="flex items-center gap-2">
      <i className={`${icono} ${color} text-[10px] w-3.5 text-center`}></i>
      <span className="text-[10px] text-foreground-500 w-16">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-background-200 overflow-hidden">
        <div className={`h-full rounded-full ${color.replace('text-', 'bg-')}`} style={{ width: `${pct}%` }}></div>
      </div>
      <span className="text-[10px] font-bold text-foreground-800 tabular-nums w-10 text-right">{formatearNumero(value)}</span>
    </div>
  );
}

export default function PatrocinadoresLocalesSection() {
  const [activeTab, setActiveTab] = useState<TabId>('patrocinadores');
  const [tipoFiltro, setTipoFiltro] = useState<TipoPatrocinadorLocal | 'todos'>('todos');
  const [ciudadFiltro, setCiudadFiltro] = useState<string>('todos');

  const filtrados = patrocinadoresLocales.filter(p => {
    if (tipoFiltro !== 'todos' && p.tipo !== tipoFiltro) return false;
    if (ciudadFiltro !== 'todos' && p.ciudad !== ciudadFiltro) return false;
    return true;
  }).slice(0, 12);

  const tabs: { id: TabId; label: string; icono: string }[] = [
    { id: 'patrocinadores', label: 'Patrocinadores', icono: 'ri-hand-heart-line' },
    { id: 'metricas', label: 'Métricas', icono: 'ri-bar-chart-line' },
    { id: 'reglas', label: 'Reglas', icono: 'ri-scales-line' },
  ];

  return (
    <section className="relative py-16 md:py-24 overflow-hidden bg-background-50">
      {/* Ambient glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/4 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-500/3 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold mb-5">
            <i className="ri-store-2-line text-xs"></i>
            Patrocinadores Locales
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tight leading-[1.1]">
            Negocios reales,{' '}
            <span className="text-emerald-600">radios locales</span>
          </h2>
          <p className="mt-4 text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            La radio administra sus propios patrocinadores. Negocios de barrio patrocinando radios de su zona. Métricas transparentes, gestión independiente, ganancia 100% radio.
          </p>

          {/* Key highlight */}
          <div className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-emerald-50 border border-emerald-200">
            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
              <i className="ri-check-double-line text-white text-sm"></i>
            </div>
            <div className="text-left">
              <p className="text-sm font-extrabold text-emerald-700">La Radio Administra</p>
              <p className="text-[10px] text-emerald-600">Gestión 100% independiente</p>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center">
            <div className="text-lg md:text-xl font-extrabold text-emerald-600 tabular-nums leading-tight">{mg.totalPatrocinadores}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Patrocinadores</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center">
            <div className="text-lg md:text-xl font-extrabold text-amber-600 tabular-nums leading-tight">{mg.patrocinadoresActivos}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Activos</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center">
            <div className="text-lg md:text-xl font-extrabold text-foreground-800 tabular-nums leading-tight">{formatearDinero(mg.inversionTotal)}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Inversión Total</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center">
            <div className="text-lg md:text-xl font-extrabold text-foreground-800 tabular-nums leading-tight">{formatearNumero(mg.impresionesTotales)}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Impresiones</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center hidden sm:block">
            <div className="text-lg md:text-xl font-extrabold text-emerald-600 tabular-nums leading-tight">{mg.ctrPromedio}%</div>
            <div className="text-[10px] text-foreground-400 font-medium">CTR Promedio</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center hidden lg:block">
            <div className="text-lg md:text-xl font-extrabold text-foreground-800 tabular-nums leading-tight">{formatearNumero(mg.leadsTotales)}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Leads</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 overflow-x-auto scrollbar-hide">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeTab === tab.id
                  ? 'bg-emerald-500 text-white shadow-sm'
                  : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
              }`}
            >
              <i className={`${tab.icono} text-[10px]`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Patrocinadores Tab */}
        {activeTab === 'patrocinadores' && (
          <div>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-5">
              <span className="text-xs text-foreground-500 mr-1">Tipo:</span>
              {[{ id: 'todos' as const, label: 'Todos' }, ...tiposPatrocinadorLocal.map(t => ({ id: t.id, label: t.label }))].map(f => (
                <button
                  key={f.id}
                  onClick={() => setTipoFiltro(f.id)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    tipoFiltro === f.id
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-900 border border-background-200/70'
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <span className="text-xs text-foreground-500 mx-1">·</span>
              <span className="text-xs text-foreground-500 mr-1">Ciudad:</span>
              {[{ id: 'todos', label: 'Todas' } as { id: string; label: string }, ...ciudades.map(c => ({ id: c, label: c }))].map(f => (
                <button
                  key={f.id}
                  onClick={() => setCiudadFiltro(f.id)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    ciudadFiltro === f.id
                      ? 'bg-amber-500 text-white shadow-sm'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-900 border border-background-200/70'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <p className="text-xs text-foreground-500 mb-4">{filtrados.length} patrocinadores locales</p>

            {/* Grid of patrocinadores */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {filtrados.map(p => (
                <div key={p.id} className="rounded-xl bg-background-50 border border-background-200/70 p-4 hover:border-emerald-300/50 hover:shadow-md transition-all duration-200 cursor-pointer group">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-background-200">
                      <img src={p.logoUrl} alt={p.nombreNegocio} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-bold text-foreground-950 truncate">{p.nombreNegocio}</h4>
                      <p className="text-[10px] text-foreground-500">{p.sector} · {p.ciudad}</p>
                    </div>
                  </div>
                  <TipoBadge tipo={p.tipo} />
                  <p className="text-xs text-foreground-600 mt-2 line-clamp-2 leading-relaxed">{p.descripcion}</p>

                  {/* Radio info */}
                  <div className="mt-2 flex items-center gap-2 text-[10px] text-foreground-500">
                    <img src={p.radioLogoUrl} alt="" className="w-4 h-4 rounded-full object-cover" />
                    <span>{p.radioNombre} {p.radioFrecuencia}</span>
                  </div>

                  {/* Display badges */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.displayEn.map(d => <DisplayBadge key={d} display={d} />)}
                  </div>

                  {/* Métricas rápidas */}
                  {p.metricas.ctr > 0 && (
                    <div className="mt-3 pt-3 border-t border-background-200/70 grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-[9px] text-foreground-400">CTR</p>
                        <p className="text-xs font-extrabold text-emerald-600">{p.metricas.ctr}%</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-foreground-400">Conversión</p>
                        <p className="text-xs font-extrabold text-amber-600">{p.metricas.conversionRate}%</p>
                      </div>
                    </div>
                  )}

                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-sm font-extrabold text-emerald-600">{formatearDinero(p.inversionTotal)}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      p.estado === 'activo' ? 'bg-emerald-50 text-emerald-600' : p.estado === 'pendiente' ? 'bg-amber-50 text-amber-600' : 'bg-foreground-100 text-foreground-500'
                    }`}>
                      {p.estado === 'activo' ? 'Activo' : p.estado === 'pendiente' ? 'Pendiente' : 'Finalizado'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Métricas Tab */}
        {activeTab === 'metricas' && (
          <div className="space-y-8">
            {/* Top performers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Top por inversión */}
              <div className="rounded-2xl bg-background-50 border border-background-200/70 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-emerald-600 text-sm"></i>
                  </div>
                  <h3 className="text-sm font-bold text-foreground-950">Top Inversión</h3>
                </div>
                <div className="space-y-2">
                  {topInversion.map((p, idx) => (
                    <div key={p.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-100/60 transition-colors cursor-pointer">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-extrabold tabular-nums flex-shrink-0">{idx + 1}</span>
                      <div className="w-7 h-7 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img src={p.logoUrl} alt={p.nombreNegocio} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-foreground-950 truncate">{p.nombreNegocio}</p>
                        <p className="text-[10px] text-foreground-500">{p.ciudad} · {p.radioNombre}</p>
                      </div>
                      <span className="text-xs font-extrabold text-emerald-600 tabular-nums whitespace-nowrap">{formatearDinero(p.inversionTotal)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top por rendimiento */}
              <div className="rounded-2xl bg-background-50 border border-background-200/70 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <i className="ri-line-chart-line text-amber-600 text-sm"></i>
                  </div>
                  <h3 className="text-sm font-bold text-foreground-950">Top Rendimiento (CTR)</h3>
                </div>
                <div className="space-y-2">
                  {topRendimiento.map((p, idx) => (
                    <div key={p.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-100/60 transition-colors cursor-pointer">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-100 text-amber-700 text-[10px] font-extrabold tabular-nums flex-shrink-0">{idx + 1}</span>
                      <div className="w-7 h-7 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img src={p.logoUrl} alt={p.nombreNegocio} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-foreground-950 truncate">{p.nombreNegocio}</p>
                        <p className="text-[10px] text-foreground-500">{p.ciudad}</p>
                      </div>
                      <span className="text-xs font-extrabold text-amber-600 tabular-nums whitespace-nowrap">{p.metricas.ctr}% CTR</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Metricas por display */}
            <div className="rounded-2xl bg-background-50 border border-background-200/70 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <i className="ri-layout-grid-line text-violet-600 text-sm"></i>
                </div>
                <h3 className="text-sm font-bold text-foreground-950">Métricas por Ubicación de Display</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {displaysPatrocinadorLocal.map(d => {
                  const md = metricasGlobalesPatrocinadoresLocales;
                  const dispData = patrocinadoresLocales.filter(p => p.displayEn.includes(d.id));
                  const totalInv = dispData.reduce((sum, p) => sum + p.inversionTotal, 0);
                  const totalImpr = dispData.reduce((sum, p) => sum + p.metricas.impresiones, 0);
                  return (
                    <div key={d.id} className="rounded-xl bg-background-100 p-4 text-center border border-background-200/70">
                      <div className={`w-10 h-10 mx-auto mb-2 rounded-xl ${d.bg} flex items-center justify-center`}>
                        <i className={`${d.icono} text-lg ${d.color}`}></i>
                      </div>
                      <p className="text-xs font-bold text-foreground-950">{d.label}</p>
                      <p className="text-lg font-extrabold text-emerald-600 tabular-nums mt-1">{dispData.length}</p>
                      <p className="text-[9px] text-foreground-500">patrocinios</p>
                      <p className="text-xs font-semibold text-foreground-800 mt-1">{formatearDinero(totalInv)}</p>
                      <p className="text-[9px] text-foreground-400">{formatearNumero(totalImpr)} impr.</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Reglas Tab */}
        {activeTab === 'reglas' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reglasSistemaPatrocinadoresLocales.map(regla => (
              <div key={regla.id} className={`rounded-2xl bg-gradient-to-br ${regla.gradiente} border ${regla.borde} p-5 hover:shadow-md transition-all duration-200 cursor-pointer`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl bg-white/60 flex items-center justify-center flex-shrink-0`}>
                    <i className={`${regla.icono} ${regla.colorTexto} text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground-950">{regla.titulo}</h4>
                  </div>
                </div>
                <p className="text-xs text-foreground-600 leading-relaxed">{regla.descripcion}</p>
              </div>
            ))}
          </div>
        )}

        {/* Bottom CTA + Rule */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between p-5 rounded-2xl bg-gradient-to-r from-emerald-50/80 to-amber-50/80 border border-emerald-200/50">
          <div className="flex items-center gap-4 md:gap-8 flex-wrap justify-center sm:justify-start">
            <div className="flex items-center gap-2 text-foreground-700 text-xs font-medium">
              <i className="ri-radio-line text-emerald-500 text-base"></i>
              <span>La radio <strong className="text-foreground-950">administra</strong> sus patrocinadores</span>
            </div>
            <div className="hidden sm:block w-px h-5 bg-emerald-200"></div>
            <div className="flex items-center gap-2 text-foreground-700 text-xs font-medium">
              <i className="ri-bar-chart-line text-emerald-500 text-base"></i>
              <span>Métricas <strong className="text-foreground-950">transparentes</strong> en tiempo real</span>
            </div>
            <div className="hidden sm:block w-px h-5 bg-emerald-200"></div>
            <div className="flex items-center gap-2 text-foreground-700 text-xs font-medium">
              <i className="ri-bank-line text-emerald-500 text-base"></i>
              <span>Ganancia <strong className="text-foreground-950">100% Radio</strong></span>
            </div>
          </div>
          <Link
            to="/patrocinadores-locales"
            className="mt-4 sm:mt-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold cursor-pointer transition-all whitespace-nowrap"
          >
            Ver Todos los Patrocinadores Locales
            <i className="ri-arrow-right-line text-xs"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}